import { ApplicationContext } from '@platform-ui-common/hooks/contexts/useApplication.js';
import { FulfillmentContext } from '@platform-ui-common/hooks/contexts/useFulfillment.js';
import { ProgramContext } from '@platform-ui-common/hooks/contexts/useProgram.js';
import { ApplicantLayout } from '@platform-ui/application/components/index.js';
import { useUserApplications } from '@platform-ui/application/hooks/index.js';
import { ErrorScreen } from '@platform-ui/application/pages/index.js';
import ApplicantRoutes from '@platform-ui/application/utils/ApplicantRoutes.js';
import { ReactNode } from 'react';
import { Redirect, Route, RouteProps, Switch, useParams } from 'react-router';
import ClaimRoutes from './ClaimRoutes.js';
import { ClaimFundsW9 } from './components/ClaimFundsW9/index.js';
import ClaimRoute from './components/ClaimRoute/index.js';
import ClaimProvider from './hooks/ClaimProvider.js';
import findFulfillmentApplication from './hooks/utils/findFulfillmentApplication.js';
import BankAccount from './pages/BankAccount/index.js';
import MailingAddress from './pages/MailingAddress/index.js';
import {
  AccountNumber,
  AccountType,
  EmailConfirmation,
  // biome-ignore lint/suspicious/noShadowRestrictedNames: Error component needed
  Error,
  PaymentMethodSelection,
  Primer,
  Review,
  RoutingNumber,
  Submission,
  ZellePrimer,
} from './pages/index.js';

function FulfillmentRouteContent({ children }: { children: ReactNode }): JSX.Element {
  const { fulfillmentId } = useParams<{ fulfillmentId: string }>();
  const applications = useUserApplications();
  const { fulfillment, application } = findFulfillmentApplication(applications, fulfillmentId);
  if (!fulfillment || !application) return <Redirect to={ApplicantRoutes.APPLICATION_PORTAL} />;
  return (
    <ProgramContext.Provider value={application.case.program.id}>
      <ApplicationContext.Provider value={application}>
        <FulfillmentContext.Provider value={fulfillment}>{children}</FulfillmentContext.Provider>
      </ApplicationContext.Provider>
    </ProgramContext.Provider>
  );
}

function FulfillmentRoute({ component: Component, children, ...rest }: RouteProps): JSX.Element {
  return (
    <Route
      {...rest}
      render={(routeProps): JSX.Element => (
        <FulfillmentRouteContent>
          {Component ? <Component {...routeProps} /> : children}
        </FulfillmentRouteContent>
      )}
    />
  );
}

export default function Routes(): JSX.Element {
  return (
    <ApplicantLayout>
      <Switch>
        <Route path={ClaimRoutes.BANK_ACCOUNT} component={BankAccount} />
        <FulfillmentRoute path="/claim/:fulfillmentId">
          <ClaimProvider>
            <Switch>
              <Route path={ClaimRoutes.PAYMENT_METHOD} component={PaymentMethodSelection} />
              <ClaimRoute exact path={ClaimRoutes.PRIMER} component={Primer} />
              {/* ACH/Direct Deposit Routes */}
              <ClaimRoute path={ClaimRoutes.ACH_ROUTING_NUMBER} component={RoutingNumber} />
              <ClaimRoute path={ClaimRoutes.ACH_ACCOUNT_NUMBER} component={AccountNumber} />
              <ClaimRoute path={ClaimRoutes.ACH_ACCOUNT_TYPE} component={AccountType} />

              {/* Prepaid Card/Check Routes */}
              <ClaimRoute path={ClaimRoutes.MAILING_ADDRESS} component={MailingAddress} />

              {/* Zelle Routes */}
              <ClaimRoute path={ClaimRoutes.ZELLE_PRIMER} component={ZellePrimer} />
              <ClaimRoute
                path={ClaimRoutes.ZELLE_EMAIL_CONFIRMATION}
                component={EmailConfirmation}
              />
              <ClaimRoute path={ClaimRoutes.W9_TAX_FORM} component={ClaimFundsW9} />
              <ClaimRoute path={ClaimRoutes.REVIEW} component={Review} />
              <ClaimRoute path={ClaimRoutes.SUBMISSION} component={Submission} />
              <Route path={ClaimRoutes.ERROR} component={Error} />
              <Route path="*" component={ErrorScreen} />
            </Switch>
          </ClaimProvider>
        </FulfillmentRoute>
      </Switch>
    </ApplicantLayout>
  );
}
